import { Button, Input, Modal, Row, Select, Space, Tag, Typography } from 'antd';
import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  ApiOutlined,
  QuestionOutlined,
  AlignLeftOutlined,
  PlusOutlined,
  CopyOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import './reviewQuestions.less';
import { QueryBuilder } from '../../../../components/molecules/QueryBuilder/QueryBuilder';
import {
  DataTable,
  DataTablePagination,
} from '../../../../components/molecules/DataTable/DataTable';
import { WebUxPage, WebUxPageContent, WebUxPageItem } from '../../webux-layouts/WebUxPage';
import Link from 'antd/lib/typography/Link';
import { useConnection } from '../../../../connection/Application';
import { CriteriaView } from '../../../../components/molecules/ReviewPreview/ReviewPreview';
import { PageHeader } from '@ant-design/pro-layout';
const { Text } = Typography;
const { Option } = Select;
const { confirm } = Modal;
const { TextArea } = Input;

export interface Criteria {
  id: number;
  name: string;
  type: number;
  description?: string;
  answer?: number;
  comment?: string;
}

export const ReviewQuestions: FC = () => {
  const [questionModalVisible, setQuestionModalVisible] = useState<boolean>(false);
  const [ratingType, setRatingType] = useState<number>();
  const [name, setName] = useState<string>();
  const [description, setDescription] = useState<string>();
  const connection = useConnection();
  const [query, setQuery] = useState<any>({});
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>();
  const [fetching, setFetching] = useState<boolean>(false);
  const [data, setData] = useState();
  const [pagination, setPagination] = useState<DataTablePagination>({ page: 1, size: 10 });
  const [selectedCriteria, setSelectedCriteria] = useState<Criteria>();
  const [editable, setEditable] = useState<boolean>(false);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      // eslint-disable-next-line react/display-name
      render: (name: Text, record: any) => (
        <Link
          onClick={() => {
            setSelectedCriteria(record);
            setName(record.name);
            setDescription(record.description);
            setRatingType(record.type);
            setQuestionModalVisible(true);
          }}>
          {name}
        </Link>
      ),
    },
    {
      title: 'Criteria Set',
      dataIndex: 'criteriaSet',
      key: 'criteriaSet',
      // eslint-disable-next-line react/display-name
      render: (name: Text, record: any) => (
        <Row>
          {record.criteriaSets.map((item: any, key: any) => (
            <Tag key={key} color="blue">
              {item.name}
            </Tag>
          ))}
        </Row>
      ),
    },
  ];

  const search = useCallback(
    (query: any, pagination: DataTablePagination) => {
      setLoading(true);
      connection
        .get('criteria', { ...pagination, ...query })
        .then((value) => {
          setData(value.data.map((e: any) => ({ ...e, key: e.id })));
          setTotal(value.total);
        })
        .finally(() => setLoading(false));
    },
    [connection],
  );
  const createCriteria = useCallback(() => {
    setFetching(true);
    const criteria = {
      name: name,
      type: ratingType,
      description: description,
    };
    connection
      .post('criteria', { criteria })
      .then(() => {
        search(query, pagination);
        setRatingType(undefined);
        setName('');
        setDescription('');
        setFetching(false);
        setQuestionModalVisible(false);
      })
      .catch(() => {
        setFetching(false);
      });
  }, [connection, description, name, pagination, query, ratingType, search]);

  useEffect(() => {
    search({}, { page: 0, size: 10 });
  }, [search]);

  const editCriteria = useCallback(() => {
    connection
      .patch(`criteria/${selectedCriteria?.id}`, {
        criteria: { name, type: ratingType, description },
      })
      .then(() => {
        setEditable(false);
        search(query, pagination);
      })
      .catch((e) => console.log(e));
  }, [connection, description, name, pagination, query, ratingType, search, selectedCriteria?.id]);

  const deleteCriteria = useCallback(() => {
    connection
      .delete(`criteria/${selectedCriteria?.id}`)
      .then(() => {
        setSelectedCriteria(undefined);
        setRatingType(undefined);
        setName('');
        setDescription('');
        setQuestionModalVisible(false);
        setEditable(false);
        search(query, pagination);
      })
      .catch((e) => console.log(e));
  }, [connection, pagination, query, search, selectedCriteria?.id]);

  const showDeleteConfirm = useCallback(() => {
    confirm({
      title: `Deleting Criteria !`,
      icon: <CloseCircleOutlined />,
      content: `Are you sure delete this criteria?`,
      okText: 'Yes,Delete!',
      centered: true,
      okType: 'primary',
      cancelText: 'Cancel',
      onOk() {
        deleteCriteria();
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  }, [deleteCriteria]);

  return (
    <WebUxPage>
      <PageHeader
        ghost={false}
        style={{ background: '#FFF' }}
        title="Criteria"
        subTitle="Manage criteria details"
      />
      <WebUxPageContent>
        <WebUxPageItem
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginTop: '1%',
              width: '100%',
            }}>
            <QueryBuilder
              searchPlaceholder="Search Criteria"
              search={true}
              value={query}
              onChange={setQuery}
              onSearch={(value) => search(value, pagination)}
              schema={[
                {
                  id: 'criteriaSet',
                  name: 'Criteria Set',
                  type: 'string',
                },
              ]}
            />
            <Space>
              <Button
                onClick={() => {
                  setQuery(undefined);
                  search(undefined, { page: 1, size: pagination.size });
                }}>
                Reset
              </Button>
              <Button type="primary" onClick={() => search(query, pagination)}>
                Search
              </Button>
            </Space>
          </div>
        </WebUxPageItem>
        <WebUxPageItem>
          <Row
            style={{
              alignItems: 'baseline',
              marginTop: '1%',
              justifyContent: 'flex-end',
              marginBottom: '0.5%',
            }}>
            <Button
              type="primary"
              onClick={() => {
                setQuestionModalVisible(true);
              }}>
              <PlusOutlined style={{ fontSize: '100%', color: 'white' }} />
              New Criteria
            </Button>
          </Row>
          <DataTable
            columns={columns}
            total={total}
            loading={loading}
            dataSource={data}
            onPaginationChange={(p) => {
              setPagination(p);
              if (pagination.page !== p.page || pagination.size !== p.size) {
                search(query, p);
              }
            }}
            dtPagination={pagination}
            style={{ marginTop: '1%' }}
          />
        </WebUxPageItem>
      </WebUxPageContent>

      <Modal
        title={selectedCriteria ? selectedCriteria.name : 'Add a Criteria'}
        open={questionModalVisible}
        footer={
          selectedCriteria ? (
            <Row style={{ justifyContent: 'space-between', margin: '0 1% 0 1%' }}>
              <Button onClick={() => showDeleteConfirm()} danger>
                Delete
              </Button>
              <Space>
                {editable ? (
                  <Button onClick={() => editCriteria()}>Save</Button>
                ) : (
                  <Button onClick={() => setEditable(true)}>Edit</Button>
                )}
                {editable ? (
                  <Button type="primary" onClick={() => setEditable(false)}>
                    Cancel
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    onClick={() => {
                      setSelectedCriteria(undefined);
                      setRatingType(undefined);
                      setName('');
                      setDescription('');
                      setQuestionModalVisible(false);
                    }}>
                    Close
                  </Button>
                )}
              </Space>
            </Row>
          ) : (
            <>
              <Button
                onClick={() => {
                  if (selectedCriteria) {
                    setSelectedCriteria(undefined);
                  }
                  setEditable(false);
                  setRatingType(undefined);
                  setName('');
                  setDescription('');
                  setQuestionModalVisible(false);
                }}>
                Cancel
              </Button>
              <Button
                type="primary"
                disabled={name === '' || description === '' || !ratingType}
                loading={fetching}
                onClick={() => createCriteria()}>
                Save
              </Button>
            </>
          )
        }
        destroyOnClose
        onCancel={() => {
          if (selectedCriteria) {
            setSelectedCriteria(undefined);
          }
          setEditable(false);
          setRatingType(undefined);
          setName('');
          setDescription('');
          setQuestionModalVisible(false);
        }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Space>
            <Space style={{ width: 100 }}>
              <ApiOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
              <Text type="secondary">Rating-Type</Text>
            </Space>
            {!editable && selectedCriteria ? (
              <Text style={{ marginLeft: 10 }}>{ratingType}</Text>
            ) : (
              <Select
                style={{ width: '100%' }}
                placeholder={'Select a type '}
                value={ratingType}
                bordered={false}
                onSelect={(val: number) => setRatingType(val)}>
                <Option value="5">5</Option>
                <Option value="10">10</Option>
              </Select>
            )}
          </Space>
          <Space size={18} style={{ marginTop: '1%' }}>
            <Space style={{ width: 100 }}>
              <QuestionOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
              <Text type="secondary">Criteria</Text>
            </Space>
            <div style={{ width: 400 }}>
              {!editable && selectedCriteria ? (
                <Text>{name}</Text>
              ) : (
                <TextArea
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  allowClear
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              )}
            </div>
          </Space>
          <Space size={18} style={{ marginTop: '1%' }}>
            <Space style={{ width: 100 }}>
              <CopyOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
              <Text type="secondary">Description</Text>
            </Space>
            <div style={{ width: 400 }}>
              {!editable && selectedCriteria ? (
                <Text>{description}</Text>
              ) : (
                <TextArea
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  allowClear
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              )}
            </div>
          </Space>
          {name && ratingType && (
            <>
              <div style={{ marginTop: '2%' }}>
                <Space style={{ width: 100 }}>
                  <AlignLeftOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
                  <Text type="secondary">Preview</Text>
                </Space>
              </div>
              <div className="borderBox">
                <CriteriaView
                  mode={'edit'}
                  criteria={{ name, type: ratingType, description, id: 0 }}
                />
              </div>
            </>
          )}
        </div>
      </Modal>
    </WebUxPage>
  );
};

export default ReviewQuestions;
